// src/components/AddPlayerForm.jsx
import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { addPlayer, enrichPlayerTM } from '../../services/api';
import { useFieldDefs } from '../../hooks/useFieldDefs';
import clubs from '../../data/clubs.json';
import clubsArg from '../../data/clubsArg.json';
import teammates from '../../data/teammates.json';
import countries from '../../data/country.json';
import coaches from '../../data/dts.json';
import positions from '../../data/positions.json';
import specificPositions from '../../data/specificPosition.json';
import './AddPlayerForm.css';

// El panel edita las dos poblaciones de jugadores, asi que el desplegable ofrece los clubes de
// los dos juegos. Se juntan SOLO aca: clubs.json alimenta ademas los tableros del juego global,
// el TeamSelector y el modo Fichaje, y meterle los argentinos los volveria casilla de un juego
// donde no van. El backend deduce el pool ('global' / 'arg') a partir de los clubes elegidos.
const clubesSeleccionables = { ...clubs, ...clubsArg };

const staticInitial = {
    name: '',
    country: '',
    position: '',
    birthDate: '',
    clubs: [],
    teammates: [],
    countryFlag: '',
    image: '',
    africanPlayer: false,
    asianPlayer: false,
    champions: false,
    goldenBall: false,
    europaLeague: false,
    concacafPlayer: false,
    worldChampion: false,
    euroCup: false,
    copaAmerica: false,
    olympics: false,
    maxTransferValue: 0,
    goldenBoot: false,
    clubWorldChampion: false,
    goldenBoy: false,
    coaches: [],
    specificPositions: []
};

export default function AddPlayerForm() {
    // 1) Cargo definiciones dinámicas
    const { fieldDefs: defs = [], loading, error } = useFieldDefs();

    // 2) Estado que mezcla estáticos + dinámicos
    const [player, setPlayer] = useState(staticInitial);

    // TM URL field + enrich state
    const [tmUrl, setTmUrl] = useState('');
    const [tmLoading, setTmLoading] = useState(false);

    // 3) Inicializo claves dinámicas cuando defs cambie
    useEffect(() => {
        const base = { ...staticInitial };
        defs.forEach(def => {
            if (!(def.key in base)) {
                base[def.key] = def.type === 'checkbox' ? false : '';
            }
        });
        setPlayer(base);
    }, [defs]);

    if (loading) return <div>Cargando campos personalizados…</div>;
    if (error) return <div>Error cargando campos: {error.message}</div>;

    // Manejadores genéricos
    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setPlayer(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (name === 'country') {
            setPlayer(prev => ({ ...prev, countryFlag: getCountryFlag(value) }));
        }
    };
    const handleMultiSelectChange = (e, fieldName) => {
        const { value } = e.target;
        if (!value) return;
        setPlayer(prev => ({
            ...prev,
            [fieldName]: [...new Set([...prev[fieldName], value])]
        }));
    };
    const handleRemoveSelected = (item, fieldName) => {
        setPlayer(prev => ({
            ...prev,
            [fieldName]: prev[fieldName].filter(i => i !== item)
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        // 1) Filtrar vacíos
        const filtered = Object.keys(player).reduce((acc, key) => {
            const v = player[key];
            if (v === '' || v === false) return acc;
            if (Array.isArray(v) && v.length === 0) return acc;
            acc[key] = v;
            return acc;
        }, {});

        // 2) Ajustes country y fecha
        if (filtered.country) {
            const code = filtered.country.toLowerCase();
            filtered.country = countries[code] || filtered.country;
        }
        if (filtered.birthDate) {
            const [y, m, d] = filtered.birthDate.split('-');
            filtered.birthDate = `${d}.${m}.${y}`;
        }

        // 3) Payload en español + dinámicos
        const payload = {
            nombre: filtered.name,
            nombre_lowercase: filtered.name?.toLowerCase() || '',
            pais: filtered.country,
            posicion: filtered.position,
            fecha_nacimiento: filtered.birthDate,
            clubes: filtered.clubs,
            teammates: filtered.teammates,
            img_pais: filtered.countryFlag,
            img: filtered.image,
            african_player: Boolean(filtered.africanPlayer),
            asian_player: Boolean(filtered.asianPlayer),
            champions: Boolean(filtered.champions),
            balon_oro: Boolean(filtered.goldenBall),
            europa_league: Boolean(filtered.europaLeague),
            concacaf_player: Boolean(filtered.concacafPlayer),
            campeon_mundo: Boolean(filtered.worldChampion),
            eurocopa: Boolean(filtered.euroCup),
            copa_america: Boolean(filtered.copaAmerica),
            jjoo: Boolean(filtered.olympics),
            maxTransferValue: filtered.maxTransferValue,
            bota_oro: Boolean(filtered.goldenBoot),
            campeon_mundo_clubes: Boolean(filtered.clubWorldChampion),
            goldenboy: Boolean(filtered.goldenBoy),
            dirigidoPor: filtered.coaches,
            specificPosition: filtered.specificPositions,
            skipped: false,
            ...defs.reduce((o, def) => {
                if (def.key in filtered) o[def.key] = filtered[def.key];
                return o;
            }, {})
        };

        try {
            const result = await addPlayer(payload);
            const newId = result?.player?._id;

            // If TM URL was provided, enrich immediately after creating
            if (newId && tmUrl.trim()) {
                setTmLoading(true);
                try {
                    const tmResult = await enrichPlayerTM(newId, tmUrl.trim());
                    toast.success(`Player added & enriquecido con TM — ${tmResult.fichajes} fichajes, ${tmResult.logros_detallados} logros`);
                } catch (tmErr) {
                    toast.warning(`Player creado pero TM falló: ${tmErr.message}`);
                } finally {
                    setTmLoading(false);
                }
            } else {
                toast.success('Player added successfully');
            }

            // Reset form
            setPlayer({
                ...staticInitial,
                ...defs.reduce((b, d) => ({ ...b, [d.key]: d.type === 'checkbox' ? false : '' }), {})
            });
            setTmUrl('');
        } catch {
            toast.error('Error adding player');
        }
    };

    const getCountryFlag = code => `https://flagcdn.com/16x12/${code}.png`;

    // Componentes auxiliares
    const SelectField = ({ label, name, options, value, onChange, multiple, placeholder, selectedItems, onRemove }) => (
        <div className="form-group">
            <label htmlFor={name}>{label}</label>
            <select name={name} id={name} value={value} onChange={onChange} className="form-select">
                <option value="">{placeholder}</option>
                {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            {multiple && selectedItems && (
                <div className="selected-items">
                    {selectedItems.map((item, i) => (
                        <div key={i} className="selected-item">
                            {item} <button type="button" onClick={() => onRemove(item, name)}>x</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
    const CheckToggle = ({ label, name, value, onChange }) => (
        <div className="check-toggle">
            <label htmlFor={name} className="check-label">{label}</label>
            <div className="checkbox-container">
                <input type="checkbox" id={name} name={name} checked={value} onChange={onChange} />
                <label htmlFor={name} className="checkbox-label"></label>
            </div>
        </div>
    );

    // … importaciones, estado y hooks idénticos …

    return (
        <div className="form-container">
            <ToastContainer />
            <form onSubmit={handleSubmit} className="player-form">

                {/* ==== BLOQUE ESTÁTICO ==== */}
                <div className="form-columns">
                    <div className="left-column">
                        {/* Name */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input type="text" name="name" id="name"
                                    placeholder="Name" value={player.name}
                                    onChange={handleChange} required className="form-input" />
                            </div>
                            {/* Country */}
                            <SelectField
                                label="Country"
                                name="country"
                                value={player.country}
                                onChange={handleChange}
                                placeholder="Select a country"
                                options={Object.entries(countries).map(([code, name]) => ({ value: code, label: name }))}
                            />
                        </div>
                        {/* Specific + Position */}
                        <div className="form-row">
                            <SelectField
                                label="Specific Position"
                                name="specificPositions"
                                value=""
                                onChange={e => handleMultiSelectChange(e, 'specificPositions')}
                                placeholder="Select a specific position"
                                options={Object.keys(specificPositions).map(p => ({ value: p, label: p }))}
                                multiple selectedItems={player.specificPositions} onRemove={handleRemoveSelected}
                            />
                            <SelectField
                                label="Position"
                                name="position"
                                value={player.position}
                                onChange={handleChange}
                                placeholder="Select a position"
                                options={Object.keys(positions).map(p => ({ value: p, label: p }))}
                            />
                        </div>
                        {/* Birth + Coaches */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="birthDate">Birth Date</label>
                                <input type="date" name="birthDate" id="birthDate"
                                    value={player.birthDate} onChange={handleChange}
                                    required className="form-input" />
                            </div>
                            <SelectField
                                label="Coaches"
                                name="coaches"
                                value=""
                                onChange={e => handleMultiSelectChange(e, 'coaches')}
                                placeholder="Select coaches"
                                options={Object.keys(coaches).map(c => ({ value: c, label: c }))}
                                multiple selectedItems={player.coaches} onRemove={handleRemoveSelected}
                            />
                        </div>
                        {/* Clubs + Teammates */}
                        <div className="form-row">
                            <SelectField
                                label="Clubs"
                                name="clubs"
                                value=""
                                onChange={e => handleMultiSelectChange(e, 'clubs')}
                                placeholder="Select clubs"
                                options={Object.keys(clubesSeleccionables).map(c => ({ value: c, label: c }))}
                                multiple selectedItems={player.clubs} onRemove={handleRemoveSelected}
                            />
                            <SelectField
                                label="Teammates"
                                name="teammates"
                                value=""
                                onChange={e => handleMultiSelectChange(e, 'teammates')}
                                placeholder="Select teammates"
                                options={Object.keys(teammates).map(t => ({ value: t, label: t }))}
                                multiple selectedItems={player.teammates} onRemove={handleRemoveSelected}
                            />
                        </div>
                        {/* Value + Image */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="maxTransferValue">Transfer Value</label>
                                <input type="number" name="maxTransferValue" id="maxTransferValue"
                                    value={player.maxTransferValue} onChange={handleChange}
                                    className="form-input" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="image">Image URL</label>
                                <input type="text" name="image" id="image"
                                    placeholder="Image URL" value={player.image}
                                    onChange={handleChange} className="form-input" />
                            </div>
                        </div>
                    </div>



                    <div className="right-column">
                        <div className="check-grid">
                            {/* — Checkbox estáticos — */}
                            {[
                                ['africanPlayer', 'African Player'],
                                ['asianPlayer', 'Asian Player'],
                                ['champions', 'Champions'],
                                ['goldenBall', 'Golden Ball'],
                                ['europaLeague', 'Europa League'],
                                ['concacafPlayer', 'Concacaf Player'],
                                ['worldChampion', 'World Champion'],
                                ['euroCup', 'Euro Cup'],
                                ['copaAmerica', 'Copa America'],
                                ['olympics', 'Olympics'],
                                ['goldenBoot', 'Golden Boot'],
                                ['clubWorldChampion', 'Club World Champion'],
                                ['goldenBoy', 'Golden Boy']
                            ].map(([key, label]) => (
                                <CheckToggle
                                    key={key}
                                    label={label}
                                    name={key}
                                    value={player[key]}
                                    onChange={handleChange}
                                />
                            ))}

                            {/* — Checkbox dinámicos — */}
                            {defs
                                .filter(def => def.type === 'checkbox')
                                .map(def => (
                                    <CheckToggle
                                        key={def.key}
                                        label={def.label}
                                        name={def.key}
                                        value={player[def.key]}
                                        onChange={handleChange}
                                    />
                                ))
                            }
                        </div>
                    </div>
                </div>

                {/* ==== BLOQUE PARA CAMPOS DINÁMICOS NO-CHECKBOX ==== */}
                {defs.some(def => def.type !== 'checkbox') && (
                    <fieldset className="dynamic-fields">
                        <legend>Campos Personalizados</legend>
                        {defs
                            .filter(def => def.type !== 'checkbox')
                            .map(def => {
                                const val = player[def.key];
                                const onChange = e => {
                                    const { name, type, checked, value } = e.target;
                                    setPlayer(prev => ({
                                        ...prev,
                                        [name]: type === 'checkbox' ? checked : value
                                    }));
                                };

                                if (def.type === 'select') {
                                    return (
                                        <SelectField
                                            key={def.key}
                                            label={def.label}
                                            name={def.key}
                                            value={val}
                                            onChange={onChange}
                                            placeholder={`Seleccione ${def.label}`}
                                            options={def.options.map(o => ({ value: o, label: o }))}
                                        />
                                    );
                                }

                                // text, number, date
                                return (
                                    <div className="form-group" key={def.key}>
                                        <label htmlFor={def.key}>{def.label}</label>
                                        <input
                                            id={def.key}
                                            name={def.key}
                                            type={def.type}
                                            value={val}
                                            onChange={onChange}
                                            className="form-input"
                                        />
                                    </div>
                                );
                            })
                        }
                    </fieldset>
                )}

                {/* ── Transfermarkt URL (opcional) ── */}
                <div className="form-group" style={{ marginTop: '12px' }}>
                    <label htmlFor="tmUrl">Transfermarkt URL <span style={{ fontWeight: 400, opacity: 0.6 }}>(opcional — enriquece al crear)</span></label>
                    <input
                        type="text"
                        id="tmUrl"
                        name="tmUrl"
                        className="form-input"
                        placeholder="https://www.transfermarkt.com/.../profil/spieler/XXXXX"
                        value={tmUrl}
                        onChange={e => setTmUrl(e.target.value)}
                    />
                </div>

                <button type="submit" className="form-button" disabled={tmLoading}>
                    {tmLoading ? 'Enriqueciendo con TM…' : 'Add Player'}
                </button>
            </form>
        </div>
    );
}

